import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

/**
 * What the page tells embed.js on the practice's website (public/embed.js reads exactly these). `events`: the
 * definition's dataLayer events of the page or the completion; an `event` is one the page itself has (a slot held, a
 * refused page with its field ids, never their answers).
 */
export type EmbedMessage =
  | { events?: string[]; page: string; type: 'page' }
  | { events?: string[]; type: 'completed' }
  | { fields?: string[]; name: string; page?: string; type: 'event' }
  | { height: number; type: 'height' }
  | { top: number; type: 'reveal' }
  | { events?: string[]; type: 'navigate'; url: string };

/** What embed.js tells the page (public/embed.js sends exactly this) */
interface EmbedInbound {
  source: 'tpm-embed';
  type: 'attribution';
  values: Record<string, unknown>;
}

/**
 * The public app inside a practice's website, put there by embed.js: no own header or background, and the height,
 * the pages shown and where to go after the form go to the embedding page by postMessage. embed.js passes that page
 * as ?embedded_on=; messages go to its origin only.
 */
@Injectable({ providedIn: 'root' })
export class EmbedService {
  readonly #window = inject(DOCUMENT).defaultView;
  /** In an iframe of another page */
  readonly embedded: boolean;
  /** The page the form sits on (embed.js), without its query */
  readonly embeddingPage: null | string;
  /** The site asked for the "powered by Beavivo" line (data-powered-by); never on a page of its own */
  readonly poweredBy: boolean;
  readonly #targetOrigin: null | string;
  readonly #attribution = signal<null | Record<string, string>>(null);
  /**
   * utm_term and the click ids as embed.js last passed them, after the visitor's "Marketing" consent on the practice's
   * page ({} once withdrawn); null while it passed none. Page submits carry them.
   */
  readonly attribution = this.#attribution.asReadonly();

  constructor() {
    const window = this.#window;
    this.embedded = !!window && window.self !== window.top;
    const query = new URLSearchParams(window?.location.search ?? '');
    const page = this.embedded ? query.get('embedded_on') : null;
    this.poweredBy = this.embedded && query.get('powered_by') === 'beavivo';
    this.embeddingPage = page && /^https?:\/\//.test(page) ? page : null;
    this.#targetOrigin = this.embeddingPage
      ? new URL(this.embeddingPage).origin
      : null;
    if (this.embedded && window) {
      window.document.documentElement.dataset['embedded'] = '';
      if (this.#targetOrigin) {
        // embed.js gives the frame the content's height (styles.css)
        window.document.documentElement.dataset['embedSized'] = '';
      }
      this.#reportHeight(window);
      window.addEventListener('message', event => this.#receive(event, window));
    }
  }

  post(message: EmbedMessage): void {
    if (this.#targetOrigin) {
      this.#window?.parent.postMessage(
        { ...message, source: 'tpm-form' },
        this.#targetOrigin
      );
    }
  }

  /** Only from the embedding page, as embed.js sends it */
  #receive(event: MessageEvent, window: Window): void {
    const message = event.data as null | Partial<EmbedInbound>;
    if (
      !this.#targetOrigin ||
      event.origin !== this.#targetOrigin ||
      event.source !== window.parent ||
      message?.source !== 'tpm-embed' ||
      message.type !== 'attribution' ||
      typeof message.values !== 'object' ||
      message.values === null
    ) {
      return;
    }
    this.#attribution.set(
      Object.fromEntries(
        Object.entries(message.values).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string'
        )
      )
    );
  }

  /** The iframe takes the content's height on every change, so the practice's page scrolls, not the frame */
  #reportHeight(window: Window): void {
    const body = window.document.body;
    if (typeof ResizeObserver === 'undefined' || !body) {
      return;
    }
    new ResizeObserver(() =>
      this.post({
        height: Math.ceil(body.getBoundingClientRect().height),
        type: 'height',
      })
    ).observe(body);
  }
}
